import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home,
  Activity,
  Info,
  Phone,
  Calendar,
  ChevronDown,
  ChevronRight,
  HeartPulse,
  Stethoscope,
  Brain,
  ActivitySquare,
  BriefcaseMedical,
  Syringe, // ✅ replaced Needle
} from "lucide-react";
import logo from "/img/procesta.png";
import { Link } from 'react-router-dom';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);

  const menuItems = [
    { name: "Home", icon: <Home size={18} />, link: "/" },
    {
      name: "Treatments",
      icon: <Activity size={18} />,
      link: "/treatments"
    },
	{ name: "Doctors", icon: <Info size={18} />, link: "/doctors" },
    { name: "About", icon: <Info size={18} />, link: "/about" },
    { name: "Contact", icon: <Phone size={18} />, link: "/contact" },
    {
      name: "Make an Appointment",
      icon: <Calendar size={18} />,
      link: "/appointment",
    },
  ];

  const handleMenuClick = (index) => {
    setActiveMenu(activeMenu === index ? null : index);
  };

  return (
    <header className="bg-gradient-to-r from-blue-600 to-blue-400 shadow-lg fixed w-full z-50">
      <div className="container mx-auto flex justify-between items-center p-4">
        {/* Logo Section */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          className="flex items-center gap-3"
        >
          <img src={logo} alt="Procesta Logo" className="h-12 w-12" />
          <h1 className="text-white font-bold text-xl tracking-wide">
            Procesta Physiotherapy Hospital
          </h1>
        </motion.div>

        {/* Desktop Menu */}
        <nav className="hidden md:flex gap-6 text-white font-medium">
          {menuItems.map((item, index) => (
            <div key={index} className="relative">
              <Link
				to={item.link}
                onClick={() => handleMenuClick(index)}
                className="flex items-center gap-2 hover:text-yellow-300 transition-all"
              >
                {item.icon}
                {item.name}
                {item.subMenu && (
                  <ChevronDown
                    size={16}
                    className={`transition-transform ${
                      activeMenu === index ? "rotate-180" : ""
                    }`}
                  />
                )}
              </Link>

              {/* Dropdown */}
              <AnimatePresence>
                {activeMenu === index && item.subMenu && (
                  <motion.ul
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                    className="absolute bg-white text-gray-700 shadow-md mt-2 rounded-lg w-72"
                  >
                    {item.subMenu.map((sub, idx) => (
                      <a
                        href={sub.link}
                        key={idx}
                        className="flex items-center gap-2 px-4 py-2 hover:bg-blue-100 border-b last:border-none"
                      >
                        {sub.icon}
                        <span>{sub.name}</span>
                        <ChevronRight
                          size={14}
                          className="ml-auto text-gray-400"
                        />
                      </a>
                    ))}
                  </motion.ul>
                )}
              </AnimatePresence>
            </div>
          ))}
        </nav>

        {/* Mobile Menu Button */}
        <div
          className="md:hidden text-white cursor-pointer"
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-7 w-7"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-7 w-7"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </div>
      </div>

      {/* Mobile Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="md:hidden bg-blue-500 text-white text-center shadow-md"
          >
            {menuItems.map((item, index) => (
              <div key={index} className="border-t border-blue-400 p-3">
                <Link
				  to={item.link}
                  onClick={() => handleMenuClick(index)}
                  className="flex items-center justify-center gap-2 w-full"
                >
                  {item.icon}
                  {item.name}
                  {item.subMenu && (
                    <ChevronDown
                      size={14}
                      className={`ml-1 transition-transform ${
                        activeMenu === index ? "rotate-180" : ""
                      }`}
                    />
                  )}
                </Link>
                {activeMenu === index && item.subMenu && (
                  <motion.ul
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="mt-2 space-y-1"
                  >
                    {item.subMenu.map((sub, idx) => (
                      <a
                        key={idx}
                        href={sub.link}
                        className="flex items-center justify-center gap-2 text-sm text-gray-100 hover:text-yellow-200"
                      >
                        {sub.icon}
                        {sub.name}
                      </a>
                    ))}
                  </motion.ul>
                )}
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
